/**
 * messagerie.js
 * -------------
 * Messagerie de direction et diffusions directes.
 *
 * Extrait de `directeurApi.js` (559 lignes, toutes les fonctions ajoutees a la
 * suite). Chaque lot ajoutait ses appels en fin de fichier : deux lots ouverts
 * en meme temps entraient donc systematiquement en conflit sur ce fichier.
 * `directeurApi.js` reste le point d'entree — il re-exporte ces modules — pour
 * qu'aucun ecran n'ait a changer ses imports.
 */
import axiosClient from "../axiosClient";

/* ── Messagerie de direction (/api/messages/) ── */

/** GET /messages/ — messages de l'utilisateur connecté (reçus et envoyés). */
export async function getMessages() {
  const { data } = await axiosClient.get("/messages/");
  return Array.isArray(data) ? data : data?.results || [];
}

/**
 * POST /messages/ — répond à un correspondant (l'expéditeur est le connecté).
 * `eleve_concerne` rattache le message au dossier d'un élève : il apparaît alors
 * dans l'onglet « Suivi & incidents » de ce dossier.
 */
export async function envoyerMessage({ destinataire, sujet, contenu, eleve_concerne }) {
  const corps = { destinataire, sujet, contenu };
  if (eleve_concerne != null) corps.eleve_concerne = eleve_concerne;
  const { data } = await axiosClient.post("/messages/", corps);
  return data;
}

/** PATCH /messages/<id>/ — marque un message comme lu. */
export async function marquerMessageLu(id) {
  const { data } = await axiosClient.patch(`/messages/${id}/`, { is_read: true });
  return data;
}

/**
 * PATCH /messages/<id>/archive/ — archive ou désarchive un fil.
 * Action dédiée du MessageViewSet (permission IsMessageParticipantOrStaff).
 */
export async function archiverMessage(id, archived = true) {
  const { data } = await axiosClient.patch(`/messages/${id}/archive/`, { is_archived: archived });
  return data;
}

/**
 * GET /utilisateurs/messaging-contacts/ — contacts joignables par l'utilisateur
 * connecté, utilisé pour résoudre les identifiants d'expéditeur/destinataire en
 * noms (le serializer de Message ne renvoie que des ids). `full_name` est déjà
 * calculé côté serveur.
 *
 * Remplace l'appel initial à /utilisateurs/ (toute la table de l'établissement,
 * >10 Mo côté backend) — l'endpoint dédié existe précisément pour éviter ce
 * payload, cf. son docstring dans `views_utilisateur.py`.
 */
export async function getContactsMessagerie() {
  const { data } = await axiosClient.get("/utilisateurs/messaging-contacts/");
  return Array.isArray(data) ? data : data?.results || [];
}

/** POST /messages/admin/broadcast-roles/ — annonce à un ou plusieurs rôles. */
export async function diffuserAnnonceRoles(roles, { sujet = "", contenu }) {
  const { data } = await axiosClient.post("/messages/admin/broadcast-roles/", { roles, sujet, contenu });
  return data;
}

/** POST /messages/admin/broadcast-classe/ — annonce ciblée sur une classe. */
export async function diffuserAnnonceClasse(classe, { sujet = "", contenu }) {
  const { data } = await axiosClient.post("/messages/admin/broadcast-classe/", { classe, sujet, contenu });
  return data;
}
